import { GridColumns, GridNativeColTypes } from '@mui/x-data-grid-pro'
import React, { useCallback } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  invalidateGlobalDocument,
  useGetApprovedGlobalDocument,
  useGetGlobalDocumentForApproval,
  useWithdrawGlobalDocumentVersion,
} from '../../../api/global-documents'
import { invalidateCustomerCache } from 'src/api/customer-admin'
import { MainButton } from '../../../components/MainButton'
import { IForApprovalGlobalDocument } from '../../../generated-types/global-documents'
import { AlertDialog } from '../../../show-dialog'
import { GlobalDocumentDialog } from './GlobalDocumentDialog'
import { toast } from 'react-toastify'
import { SUCCESSFULLY_WITHDREW } from '../../../constant'
import { useDialogCtx } from '../../../hooks/context-hooks'
import { CustomStyledTable } from 'src/screens/localities/moorings/Components/PositionTable'
import { getFormattedDate } from 'src/utils'
import EditIcon from '@mui/icons-material/Edit'
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl'
import { GlobalDocumentReadApprovalDialog } from './GlobalDocumentReadApprovalDialog'
import { GlobalDocumentApprovalStatus } from './GlobalDocumentApprovalStatus'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { invalidateDocument } from 'src/api/document'
import { CenteredSpinner } from 'src/components/Spinner'

export const invalidateAllGlobalDocuments = () => {
  invalidateGlobalDocument.forApproval()
  invalidateGlobalDocument.approved()
  invalidateGlobalDocument.getCategories()
  invalidateGlobalDocument.getCounts()
}

export const GlobalDocumentsTable: React.FC<{
  customerId: EntityId
  documentCategoryId?: EntityId
  IsApprovedDocs: boolean
  height?: number | string
  autoHeight?: boolean
  rowHeight?: number
}> = ({ customerId, documentCategoryId, IsApprovedDocs, height, autoHeight, rowHeight }) => {
  const { mutateAsync: withdrawDocument } = useWithdrawGlobalDocumentVersion()
  const { t } = useTranslation(['global-documents', 'common', 'document'])
  const { showDialog } = useDialogCtx()
  const { data: globalDocumentsForApproval, isLoading: isGlobalDocumentsForApprovalLoading } =
    useGetGlobalDocumentForApproval(
      {
        customerId,
        documentCategoryId,
      },
      !IsApprovedDocs,
    )

  const { data: globalDocumentsApproved, isLoading: isGlobalDocumentsApprovedLoading } = useGetApprovedGlobalDocument(
    {
      customerId,
      documentCategoryId,
    },
    IsApprovedDocs,
  )

  const onApprove = useCallback(
    async (document: IForApprovalGlobalDocument) => {
      const added = await showDialog(
        GlobalDocumentDialog,
        {
          opts: {
            maxWidth: 'lg',
            fullWidth: true,
          },
          componentProps: {
            customerId: customerId as EntityId,
            globalDocument: document,
            pop: () => {},
            title: t('for-approval', { ns: 'global-documents' }),
          },
          isAutoCloseDisabled: true,
        },
        undefined,
        true,
      )
      if (!added) return
    },
    [customerId, showDialog, t],
  )

  const onWithdraw = useCallback(
    async (document: IForApprovalGlobalDocument) => {
      const added = await showDialog(
        GlobalDocumentDialog,
        {
          opts: {
            maxWidth: 'lg',
            fullWidth: true,
          },
          componentProps: {
            title: `${t('withdraw-document', { ns: 'global-documents' })}`,
            customerId: customerId as EntityId,
            globalDocument: document,
            pop: () => {},
          },
          isAutoCloseDisabled: true,
        },
        undefined,
        true,
      )
      if (!added) return
    },
    [customerId, showDialog, t],
  )

  const handleWithdraw = useCallback(
    async row => {
      showDialog(AlertDialog, {
        componentProps: {
          title: t('withdraw-document', { ns: 'global-documents' }),
          acceptText: t('withdraw', { ns: 'global-documents' }),
          text: t('areYouSureWithdrawDocument', { ns: 'global-documents' }),
          onConfirm: async pop => {
            withdrawDocument({ customerId: customerId, documentVersionId: row.documentVersionId }).then(
              ({ data }) => {
                if (data === SUCCESSFULLY_WITHDREW) {
                  toast.success(t('successfullyWithdrew', { ns: 'global-documents' }))
                }
                invalidateAllGlobalDocuments()
                invalidateCustomerCache.getDocumentReadStatus(customerId, Number(row.id))
                invalidateCustomerCache.getUserDocumentVersionReadStatus(customerId, Number(row.id))
                invalidateDocument.getDocuments(customerId)
                invalidateCustomerCache.getDocumentReadStatus(customerId)
                invalidateCustomerCache.getUserDocumentVersionReadStatus(customerId)
                invalidateDocument.getDocumentsForApproval(customerId)
                pop()
              },
              () => {
                toast.error(t('unExpectedError', { ns: 'common-api-message' }))
              },
            )
          },
        },
      })
    },
    [showDialog, t, withdrawDocument, customerId],
  )

  const handleEdit = useCallback(
    async row => {
      showDialog(GlobalDocumentReadApprovalDialog, {
        title: t('update-document', { ns: 'document' }),
        componentProps: {
          documentId: row.id,
          customerId: customerId,
          isReadApprovalRequired: row.isReadApprovalRequired,
          pop: () => {},
        },
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
      })
    },
    [showDialog, customerId],
  )

  const handleOpenReadStatus = useCallback(
    async row => {
      showDialog(GlobalDocumentApprovalStatus, {
        title: t('user-read-status', { ns: 'document' }),
        componentProps: {
          documentId: row.id,
          customerId: customerId,
          pop: () => {},
        },
        opts: {
          maxWidth: 'md',
          fullWidth: true,
        },
      })
    },
    [showDialog, customerId],
  )

  const staticColumn: GridColumns = useMemo(
    () => [
      { field: 'title', headerName: t('title', { ns: 'common' }), flex: 1 },
      {
        field: 'version',
        headerName: t('version', { ns: 'global-documents' }),
        flex: 1,
        valueGetter: ({ value }) => (value ? value : ' - '),
      },
      {
        field: 'supplierName',
        headerName: t('supplier', { ns: 'global-documents' }),
        flex: 1,
        valueGetter: ({ value }) => (value ? value : ' - '),
      },
    ],
    [t],
  )

  const columnsForPending: GridColumns = useMemo(
    () => [
      {
        field: 'createdAt',
        headerName: t('createdAt', { ns: 'global-documents' }),
        flex: 1,
        valueGetter: value => (value?.value ? getFormattedDate(value?.value) : ' - '),
      },
    ],
    [t],
  )

  const columnsForApproved: GridColumns = useMemo(
    () => [
      {
        field: 'approvedAt',
        headerName: t('approvedAt', { ns: 'global-documents' }),
        flex: 1,
        valueGetter: value => (value?.value ? getFormattedDate(value?.value) : ' - '),
      },
      {
        field: 'approvedBy',
        headerName: t('approvedBy', { ns: 'global-documents' }),
        flex: 1,
        valueGetter: ({ value }) => (value ? value : ' - '),
      },
      {
        field: 'more-action',
        type: 'actions' as GridNativeColTypes,
        width: 300,
        getActions: ({ row }) => [
          <MainButton
            key="withdraw"
            variant="contained"
            sx={{ letterSpacing: 1, height: '30px' }}
            onClick={() => {
              handleWithdraw(row)
            }}
          >
            {t('withdraw', { ns: 'global-documents' })}
          </MainButton>,
          <GridActionButton
            key="edit"
            toolTipTitle={t('edit', { ns: 'common' })}
            buttonIcon={<EditIcon />}
            onClick={() => handleEdit(row)}
            width="30px"
            height="30px"
          />,
          <GridActionButton
            key="read-status"
            toolTipTitle={t('user-read-status', { ns: 'document' })}
            buttonIcon={<ChecklistRtlIcon />}
            onClick={() => handleOpenReadStatus(row)}
            disabled={!row.isReadApprovalRequired}
            width="30px"
            height="30px"
          />,
        ],
      },
    ],
    [handleWithdraw, handleEdit, handleOpenReadStatus, t],
  )

  const columns: GridColumns = useMemo(
    () => [...staticColumn, ...(IsApprovedDocs ? columnsForApproved : columnsForPending)],
    [IsApprovedDocs, columnsForApproved, columnsForPending, staticColumn],
  )

  if ((IsApprovedDocs && isGlobalDocumentsApprovedLoading) || isGlobalDocumentsForApprovalLoading) {
    return <CenteredSpinner size={24} />
  }

  return (
    <CustomStyledTable
      id="global-document-table"
      columns={columns}
      data={IsApprovedDocs ? globalDocumentsApproved ?? [] : globalDocumentsForApproval ?? []}
      onRowClick={({ row }) => {
        if (IsApprovedDocs) {
          onWithdraw(row)
        } else {
          onApprove(row)
        }
      }}
      getRowId={c => c.id}
      getRowHeight={() => rowHeight ?? 'auto'}
      enableToolbar={true}
      noRowsMessage={t('no-documents', { ns: 'global-documents' })}
      autoHeight={autoHeight ?? false}
      height={height}
      minRowHeight="36px"
    />
  )
}
